import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import moment, { Moment } from "moment-timezone";
import { PaginationState, SortingState } from "@tanstack/react-table";
import { t } from "@lingui/core/macro";

import {
    Channel,
    Fulfillment,
    PaymentMethod,
    Provider,
} from "#sales-report/types";

// -- Defined dates enum (owned here, re-exported from orders-context) --

export enum DefinedDates {
    TODAY = "Today",
    YESTERDAY = "Yesterday",
    LAST7DAYS = "Last 7 days",
    LAST30DAYS = "Last 30 days",
    LAST90DAYS = "Last 90 days",
    LAST365DAYS = "Last 365 days",
    SPECIFIC_DAY = "Specific Day",
    DATE_RANGE = "Date Range",
}

export function getDefinedDatesTranslations() {
    return {
        [DefinedDates.TODAY]: t`Today`,
        [DefinedDates.YESTERDAY]: t`Yesterday`,
        [DefinedDates.LAST7DAYS]: t`Last 7 days`,
        [DefinedDates.LAST30DAYS]: t`Last 30 days`,
        [DefinedDates.LAST90DAYS]: t`Last 90 days`,
        [DefinedDates.LAST365DAYS]: t`Last 365 days`,
        [DefinedDates.SPECIFIC_DAY]: t`Specific Day`,
        [DefinedDates.DATE_RANGE]: t`Date Range`,
    };
}

// -- Constants --

const DEFAULT_SORT_ID = "dateReceived";
const DEFAULT_PAGE_SIZE = 10;

const RANGE_TO_DURATION: Record<string, DefinedDates> = {
    today: DefinedDates.TODAY,
    yesterday: DefinedDates.YESTERDAY,
    last7: DefinedDates.LAST7DAYS,
    last30: DefinedDates.LAST30DAYS,
    last90: DefinedDates.LAST90DAYS,
    last365: DefinedDates.LAST365DAYS,
    day: DefinedDates.SPECIFIC_DAY,
    custom: DefinedDates.DATE_RANGE,
};

const DURATION_TO_RANGE: Record<DefinedDates, string> = {
    [DefinedDates.TODAY]: "today",
    [DefinedDates.YESTERDAY]: "yesterday",
    [DefinedDates.LAST7DAYS]: "last7",
    [DefinedDates.LAST30DAYS]: "last30",
    [DefinedDates.LAST90DAYS]: "last90",
    [DefinedDates.LAST365DAYS]: "last365",
    [DefinedDates.SPECIFIC_DAY]: "day",
    [DefinedDates.DATE_RANGE]: "custom",
};

// -- Helpers --

const parseList = (value: string | null): string[] =>
    value ? value.split(",").filter(Boolean) : [];

const deriveDates = (
    duration: DefinedDates,
    fromParam: string | null,
    toParam: string | null,
): { startDate: Moment; endDate: Moment } => {
    const now = moment();
    switch (duration) {
        case DefinedDates.TODAY:
            return {
                startDate: now.clone().startOf("day"),
                endDate: now.clone().endOf("day"),
            };
        case DefinedDates.YESTERDAY:
            return {
                startDate: now.clone().subtract(1, "day").startOf("day"),
                endDate: now.clone().subtract(1, "day").endOf("day"),
            };
        case DefinedDates.LAST7DAYS: {
            const end = now.clone().subtract(1, "days").endOf("day");
            const start = end.clone().subtract(6, "days").startOf("day");
            return { startDate: start, endDate: end };
        }
        case DefinedDates.LAST30DAYS: {
            const end = now.clone().subtract(1, "days").endOf("day");
            const start = end.clone().subtract(29, "days").startOf("day");
            return { startDate: start, endDate: end };
        }
        case DefinedDates.LAST90DAYS: {
            const end = now.clone().subtract(1, "days").endOf("day");
            const start = end.clone().subtract(89, "days").startOf("day");
            return { startDate: start, endDate: end };
        }
        case DefinedDates.LAST365DAYS: {
            const end = now.clone().subtract(1, "days").endOf("day");
            const start = end.clone().subtract(364, "days").startOf("day");
            return { startDate: start, endDate: end };
        }
        case DefinedDates.SPECIFIC_DAY: {
            if (fromParam) {
                const day = moment(fromParam, "YYYY-MM-DD");
                return {
                    startDate: day.clone().startOf("day"),
                    endDate: day.clone().endOf("day"),
                };
            }
            return {
                startDate: now.clone().startOf("day"),
                endDate: now.clone().endOf("day"),
            };
        }
        case DefinedDates.DATE_RANGE: {
            const start = fromParam
                ? moment(fromParam, "YYYY-MM-DD").startOf("day")
                : now.clone().startOf("day");
            const end = toParam
                ? moment(toParam, "YYYY-MM-DD").endOf("day")
                : now.clone().endOf("day");
            return { startDate: start, endDate: end };
        }
        default:
            return {
                startDate: now.clone().startOf("day"),
                endDate: now.clone().endOf("day"),
            };
    }
};

// Builds a new URLSearchParams from the current one with mutations applied.
const withParams = (
    current: URLSearchParams,
    mutate: (p: URLSearchParams) => void,
): URLSearchParams => {
    const next = new URLSearchParams(current);
    mutate(next);
    return next;
};

// -- Hook --

export const useOrdersSearchParams = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // ---- Derived state ----

    const pageIndex = Math.max(
        0,
        Number(searchParams.get("page") ?? "1") - 1,
    );
    const pageSize = Number(
        searchParams.get("pageSize") ?? String(DEFAULT_PAGE_SIZE),
    );
    const sortId = searchParams.get("sort") ?? DEFAULT_SORT_ID;
    const sortDesc = searchParams.get("dir") !== "asc";

    const pagination: PaginationState = useMemo(
        () => ({ pageIndex, pageSize }),
        [pageIndex, pageSize],
    );
    const sorting: SortingState = useMemo(
        () => [{ id: sortId, desc: sortDesc }],
        [sortId, sortDesc],
    );

    const rangeParam = searchParams.get("range");
    const fromParam = searchParams.get("from");
    const toParam = searchParams.get("to");
    const duration: DefinedDates = rangeParam
        ? (RANGE_TO_DURATION[rangeParam] ?? DefinedDates.TODAY)
        : DefinedDates.TODAY;
    const { startDate, endDate } = useMemo(
        () => deriveDates(duration, fromParam, toParam),
        [duration, fromParam, toParam],
    );

    const channelsRaw = searchParams.get("channels");
    const providersRaw = searchParams.get("providers");
    const fulfillmentsRaw = searchParams.get("fulfillments");
    const paymentRaw = searchParams.get("payment");

    const channels = useMemo(
        () => parseList(channelsRaw) as Channel[],
        [channelsRaw],
    );
    const providers = useMemo(
        () => parseList(providersRaw) as Provider[],
        [providersRaw],
    );
    const fulfillments = useMemo(
        () => parseList(fulfillmentsRaw) as Fulfillment[],
        [fulfillmentsRaw],
    );
    const paymentMethods = useMemo(
        () => parseList(paymentRaw) as PaymentMethod[],
        [paymentRaw],
    );
    const refunded = searchParams.get("refunded") === "1";

    // ---- Setters ----

    const set = useCallback(
        (mutate: (p: URLSearchParams) => void) => {
            setSearchParams(
                (prev) => {
                    const next = withParams(prev, mutate);
                    // Return same reference if nothing changed to avoid re-render loops
                    // (TanStack Table calls onPaginationChange on every render)
                    if (prev.toString() === next.toString()) {
                        return prev;
                    }
                    return next;
                },
                { replace: true, preventScrollReset: true },
            );
        },
        [setSearchParams],
    );

    const setPagination = useCallback(
        (
            updater:
                | PaginationState
                | ((prev: PaginationState) => PaginationState),
        ) => {
            const current = { pageIndex, pageSize };
            const next = updater instanceof Function ? updater(current) : updater;
            if (next.pageIndex === current.pageIndex && next.pageSize === current.pageSize) {
                return;
            }
            set((p) => {
                next.pageIndex === 0
                    ? p.delete("page")
                    : p.set("page", String(next.pageIndex + 1));
                next.pageSize === DEFAULT_PAGE_SIZE
                    ? p.delete("pageSize")
                    : p.set("pageSize", String(next.pageSize));
            });
        },
        [set, pageIndex, pageSize],
    );

    const setSorting = useCallback(
        (
            updater:
                | SortingState
                | ((prev: SortingState) => SortingState),
        ) => {
            set((p) => {
                const current: SortingState = [
                    { id: p.get("sort") ?? DEFAULT_SORT_ID, desc: p.get("dir") !== "asc" },
                ];
                const next = updater instanceof Function ? updater(current) : updater;
                if (next.length > 0 && !(next[0].id === DEFAULT_SORT_ID && next[0].desc)) {
                    p.set("sort", next[0].id);
                    p.set("dir", next[0].desc ? "desc" : "asc");
                } else {
                    p.delete("sort");
                    p.delete("dir");
                }
                p.delete("page");
            });
        },
        [set],
    );

    const setDuration = useCallback(
        (newDuration: DefinedDates) => {
            set((p) => {
                const rangeValue = DURATION_TO_RANGE[newDuration];
                if (newDuration === DefinedDates.TODAY) {
                    p.delete("range");
                } else {
                    p.set("range", rangeValue);
                }
                if (
                    newDuration === DefinedDates.TODAY ||
                    newDuration === DefinedDates.YESTERDAY ||
                    newDuration === DefinedDates.LAST7DAYS ||
                    newDuration === DefinedDates.LAST30DAYS ||
                    newDuration === DefinedDates.LAST90DAYS ||
                    newDuration === DefinedDates.LAST365DAYS
                ) {
                    p.delete("from");
                    p.delete("to");
                }
                p.delete("page");
            });
        },
        [set],
    );

    const setTimeRange = useCallback(
        ({ startDate, endDate }: { startDate: Moment; endDate: Moment }) => {
            set((p) => {
                const fromStr = startDate.format("YYYY-MM-DD");
                const toStr = endDate.format("YYYY-MM-DD");
                if (fromStr === toStr) {
                    p.set("range", "day");
                    p.set("from", fromStr);
                    p.delete("to");
                } else {
                    p.set("range", "custom");
                    p.set("from", fromStr);
                    p.set("to", toStr);
                }
                p.delete("page");
            });
        },
        [set],
    );

    const setChannels = useCallback(
        (values: Channel[]) => {
            set((p) => {
                values.length > 0 ? p.set("channels", values.join(",")) : p.delete("channels");
                p.delete("page");
            });
        },
        [set],
    );

    const setProviders = useCallback(
        (values: Provider[]) => {
            set((p) => {
                values.length > 0 ? p.set("providers", values.join(",")) : p.delete("providers");
                p.delete("page");
            });
        },
        [set],
    );

    const setFulfillments = useCallback(
        (values: Fulfillment[]) => {
            set((p) => {
                values.length > 0 ? p.set("fulfillments", values.join(",")) : p.delete("fulfillments");
                p.delete("page");
            });
        },
        [set],
    );

    const setPaymentMethods = useCallback(
        (values: PaymentMethod[]) => {
            set((p) => {
                values.length > 0 ? p.set("payment", values.join(",")) : p.delete("payment");
                p.delete("page");
            });
        },
        [set],
    );

    const setRefunded = useCallback(
        (value: boolean) => {
            set((p) => {
                value ? p.set("refunded", "1") : p.delete("refunded");
                p.delete("page");
            });
        },
        [set],
    );

    const globalFilter = searchParams.get("q") ?? "";

    const setGlobalFilter = useCallback(
        (value: string) => {
            set((p) => {
                value ? p.set("q", value) : p.delete("q");
                p.delete("page");
            });
        },
        [set],
    );

    return {
        pagination,
        setPagination,
        sorting,
        setSorting,
        startDate,
        endDate,
        duration,
        setDuration,
        setTimeRange,
        channels,
        setChannels,
        providers,
        setProviders,
        fulfillments,
        setFulfillments,
        paymentMethods,
        setPaymentMethods,
        refunded,
        setRefunded,
        globalFilter,
        setGlobalFilter,
    };
};
